import { ExpenseVatBreakdownEntry, TaxScheme } from '../../types/expenseTypes';

/**
 * `expenses/taxSchemeLabel` (M-V4-E, architecture-ux-v4.md §5.3).
 *
 * Pure derivation of the tax-rate percentages to display in `TaxSchemeChip`'s
 * short form for the `standard` scheme — replacing the previous static
 * "Regel" i18n text with the ACTUAL rate(s) taken from the expense's real
 * VAT data.
 *
 * FAIL-CLOSED / never-a-percentage contract (e-rechnung-schema-label-review.md,
 * Prüffrage 2 — "kritischster Punkt"): for every scheme other than `standard`
 * this ALWAYS returns `null`, regardless of `vatRate`/`vatBreakdown` content.
 * This is what keeps `reverse_charge_13b` (§13b) from ever rendering a
 * self-computed percentage — the caller falls back to the static
 * `taxScheme.short.<scheme>` i18n key ("§ 13b") in that case.
 *
 * For `standard`:
 *   1. Prefer the per-rate breakdown (`vatBreakdown`): take `taxRate` values
 *      greater than 0, de-duplicate, sort descending.
 *   2. If the breakdown yields nothing (undefined/empty/only-0%-lines, e.g. a
 *      Trinkgeld line), fall back to the single `vatRate` field if it is a
 *      positive number.
 *   3. If neither yields a positive rate, return `null` — the caller then
 *      shows the neutral i18n fallback text (`taxScheme.short.standard` =
 *      "Regelsatz"/"Standard rate"/…), NOT a fabricated "19 % / 7 %" claim
 *      (e-rechnung-schema-label-review.md ⚠️-Finding, standard-fallback).
 *
 * @param scheme - The expense's tax treatment scheme.
 * @param vatRate - The expense's primary VAT rate (`Expense.vatRate`), or
 *   `null`/`undefined` when multiple rates apply or the field is absent.
 * @param vatBreakdown - The expense's per-rate VAT breakdown
 *   (`Expense.vatBreakdown`), or `undefined` if absent.
 * @returns A descending, de-duplicated array of positive rates to render
 *   (`standard` scheme with resolvable rate data), or `null` when the caller
 *   should fall back to the static short-form i18n key.
 */
export function deriveTaxRateDisplay(
  scheme: TaxScheme,
  vatRate: number | null | undefined,
  vatBreakdown: ExpenseVatBreakdownEntry[] | undefined,
): number[] | null {
  // Non-standard schemes NEVER show a rate — most critically §13b, which owes
  // a self-computed VAT internally (`vatOwedCents`) that must never leak into
  // this display. Bestand: needs_review, zero_exempt, small_business_19,
  // intra_community_6a, import_third_country all use their static short key.
  if (scheme !== 'standard') return null;

  const fromBreakdown = Array.from(
    new Set(
      (vatBreakdown ?? [])
        .map((entry) => entry.taxRate)
        .filter((rate): rate is number => typeof rate === 'number' && rate > 0),
    ),
  ).sort((a, b) => b - a);

  if (fromBreakdown.length > 0) return fromBreakdown;

  if (typeof vatRate === 'number' && vatRate > 0) return [vatRate];

  return null;
}

export default deriveTaxRateDisplay;
